import { useEffect, useState } from 'react'

import { User, useScript } from '@lyrahealth-inc/shared-app-logic'

const CHECKSUM_SDK_URL = 'https://cdn.checksum.ai/checksum-sdk-min-0.3.js'

declare global {
  interface Window {
    checksumai?: {
      init: (sdkKey: string, config?: ChecksumConfig, options?: Record<string, unknown>) => void
      identify: (userId: string, userProperties?: Record<string, unknown>) => void
      reset: () => void
    }
  }
}

interface ChecksumConfig {
  sessionRecording?: {
    masking?: {
      maskSensitiveText?: boolean
      customMaskFunction?: (value: string, element?: HTMLElement) => string
    }
    hashing?: {
      hashAllInputs?: boolean
      hashAllText?: boolean
    }
  }
}

/**
 * Check if Checksum SDK should be initialized
 * Only initialize in staging and prerelease
 */
const shouldInitializeChecksum = (): boolean => {
  if (window.Cypress) {
    return false
  }

  const env = process.env.NODE_ENV as string
  return env === 'staging' || env === 'prerelease'
}

/**
 * Handles initialization and user identification for the Checksum testing tool
 *
 * @param checksumSdkKey - SDK key from appGlobals (fetched from backend)
 * @param user - Current user object
 * @returns boolean showing if checkSum is initialized
 */
const useChecksum = ({ checksumSdkKey, user }: { checksumSdkKey?: string; user: User }) => {
  const [checksumInitialized, setChecksumInitialized] = useState(false)
  const scriptStatus = useScript(shouldInitializeChecksum() ? CHECKSUM_SDK_URL : null, false, undefined)

  // Initialize Checksum SDK when script is loaded and SDK key is available
  useEffect(() => {
    if (!shouldInitializeChecksum()) {
      return
    }

    if (scriptStatus !== 'ready') {
      return
    }

    if (!checksumSdkKey) {
      return
    }

    if (!window.checksumai) {
      console.warn('[Checksum] SDK not loaded. Script may not have loaded yet.')
      return
    }

    if (checksumInitialized) {
      return
    }

    try {
      const config: ChecksumConfig = {
        sessionRecording: {
          masking: {
            maskSensitiveText: true,
          },
          hashing: {
            hashAllInputs: false,
            hashAllText: false,
          },
        },
      }

      window.checksumai.init(checksumSdkKey, config, {})
      setChecksumInitialized(true)
      console.log('[Checksum] SDK initialized successfully')
    } catch (error) {
      console.error('[Checksum] Failed to initialize SDK:', error)
    }
  }, [checksumSdkKey, checksumInitialized, scriptStatus])

  // Identify user in Checksum after SDK is initialized and user is logged in
  useEffect(() => {
    if (!checksumInitialized || !user.lyraId) {
      return
    }

    if (!window.checksumai) {
      return
    }

    try {
      window.checksumai.identify(user.lyraId)
    } catch (error) {
      console.error('[Checksum] Failed to identify user:', error)
    }
  }, [checksumInitialized, user.lyraId])

  return checksumInitialized
}

export default useChecksum
